/**
 * advance-game-end.ts — per-tick win/defeat detection (v3M12).
 *
 * Runs after combat / surface-batteries / bombardment so a planet
 * flip on this tick (Loyalty conquest or depopulation) can trigger
 * an empire elimination → outcome flip on the same tick. Per
 * `docs/WIN_CONDITIONS.md`:
 *
 *   - For each empire with zero planets owned, despawn its ships
 *     (keeps the empire row in `state.empires` so end-game stats
 *     can still attribute names + colours, but clears `shipIds`
 *     and removes the entries from `state.ships` + `teamVision`).
 *   - If the player's empire has zero planets → `gameOutcome` flips
 *     to `'defeat'`.
 *   - Else if every non-player empire has zero planets → `gameOutcome`
 *     flips to `'victory'`.
 *
 * Once `gameOutcome` is non-`'in-progress'`, the top-of-`advanceTick`
 * guard makes the whole sim a no-op — this pass becomes irrelevant.
 *
 * Determinism: pure derivation from planet ownership + empire
 * membership. No RNG, no wall-clock, no input.
 */

import type { EmpireId, GameState, ShipInstanceId } from '../state/types';
import { EMPIRE_NEUTRAL_ID } from '../state/types';
import { areTeammates, getTeamId } from './teams';

export function advanceGameEnd(state: GameState): void {
  if (state.gameOutcome !== 'in-progress') return;

  // Tally planets per empire.
  const planetsPerEmpire = new Map<EmpireId, number>();
  for (const empire of state.empires.values()) planetsPerEmpire.set(empire.id, 0);
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) continue;
    const cur = planetsPerEmpire.get(planet.ownerEmpireId) ?? 0;
    planetsPerEmpire.set(planet.ownerEmpireId, cur + 1);
  }

  // Despawn ships of any empire that's been eliminated. Idempotent —
  // an already-eliminated empire (shipIds: []) loops zero times.
  for (const empire of state.empires.values()) {
    if ((planetsPerEmpire.get(empire.id) ?? 0) > 0) continue;
    if (empire.shipIds.length === 0) continue;
    despawnEmpireShips(state, empire.id);
  }

  // v4M3.4 — outcome resolution is team-aware per `docs/TEAMS.md §5`.
  // A team is alive iff at least one of its members holds a planet.
  // Solo empires are functionally one-empire teams. The player's
  // outcome flips on the player's TEAM aliveness, not the player's
  // individual empire — a player whose empire was eliminated but
  // whose teammate goes on to wipe out the last opposing team sees
  // a "victory" screen.
  const playerTeamId = getTeamId(state, state.playerEmpireId);
  const playerTeamAlive = isTeamAlive(
    state,
    state.playerEmpireId,
    playerTeamId,
    planetsPerEmpire,
  );

  // Defeat — the player's team is fully eliminated. Defeat takes
  // precedence so simultaneous mutual elimination resolves as defeat.
  if (!playerTeamAlive) {
    state.gameOutcome = 'defeat';
    return;
  }

  // Victory — every team other than the player's is fully eliminated.
  // Solo empires (teamId === null) each count as their own one-empire
  // team for outcome purposes. The synthetic neutral never counts as
  // an enemy team (preserves the v3M21.1 carve-out).
  let hasAnyNonPlayerEmpire = false;
  let anyEnemyTeamAlive = false;
  // Track which teams we've already evaluated so we don't double-count.
  const evaluatedTeamIds = new Set<number>();
  if (playerTeamId !== null) evaluatedTeamIds.add(playerTeamId);
  for (const empire of state.empires.values()) {
    if (empire.id === state.playerEmpireId) continue;
    if (empire.id === EMPIRE_NEUTRAL_ID) continue;
    // Skip teammates of the player — they're on the player's team.
    if (areTeammates(state, empire.id, state.playerEmpireId)) continue;
    hasAnyNonPlayerEmpire = true;
    // Non-null teamId — only evaluate each enemy team once.
    if (empire.teamId !== null) {
      if (evaluatedTeamIds.has(empire.teamId)) continue;
      evaluatedTeamIds.add(empire.teamId);
    }
    if (isTeamAlive(state, empire.id, empire.teamId, planetsPerEmpire)) {
      anyEnemyTeamAlive = true;
      break;
    }
  }
  if (hasAnyNonPlayerEmpire && !anyEnemyTeamAlive) {
    state.gameOutcome = 'victory';
  }
}

/**
 * v4M3.4 — true iff at least one empire on the given team holds a
 * planet. Solo empires (`teamId === null`) check just `empireId`'s
 * own row; team viewers check every member's row and OR the results.
 */
function isTeamAlive(
  state: GameState,
  empireId: EmpireId,
  teamId: number | null,
  planetsPerEmpire: Map<EmpireId, number>,
): boolean {
  if (teamId === null) {
    return (planetsPerEmpire.get(empireId) ?? 0) > 0;
  }
  for (const [id, e] of state.empires) {
    if (e.teamId !== teamId) continue;
    if ((planetsPerEmpire.get(id) ?? 0) > 0) return true;
  }
  return false;
}

/**
 * Remove every ship owned by the given empire from `state.ships`
 * and from every team's `teamVision` row, then clear the empire's
 * own `shipIds`. The empire row stays in `state.empires` so the
 * end-game overlay can still attribute it by name + colour.
 */
function despawnEmpireShips(state: GameState, empireId: EmpireId): void {
  const empire = state.empires.get(empireId);
  if (!empire) return;
  const ids: ShipInstanceId[] = [...empire.shipIds];
  for (const id of ids) state.ships.delete(id);
  for (const inner of state.teamVision.values()) {
    for (const id of ids) inner.delete(id);
  }
  empire.shipIds = [];
}
